/**
 * Session 30.7 — Printer setup dialog (shared by Retail + Staff).
 *
 * Lets the operator pick the slip printer for THIS device and fire a
 * test slip. What it shows depends on the shell:
 *   • Android APK → paired Bluetooth printers (silent ESC/POS)
 *   • Windows EXE → installed Windows printers (silent raw spooler)
 *   • Browser/PWA → guidance for --kiosk-printing
 * Paper width (80/58 mm), copies and auto-cut apply everywhere.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bluetooth, Loader2, Printer, RefreshCw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  detectPrintShell,
  getPrinterConfig,
  savePrinterConfig,
  listBluetoothPrinters,
  listWindowsPrinters,
  printSlip,
  buildTestReceipt,
} from "@/lib/printer";

export function PrinterSettingsDialog({ open, onOpenChange, businessName, formatPrice }) {
  const [shell, setShell] = useState("browser");
  const [cfg, setCfg] = useState(() => getPrinterConfig());
  const [btDevices, setBtDevices] = useState([]);
  const [btState, setBtState] = useState({ available: true, enabled: true });
  const [winPrinters, setWinPrinters] = useState({ default: null, printers: [] });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const s = await detectPrintShell();
      setShell(s);
      if (s === "android") {
        const r = await listBluetoothPrinters();
        setBtState({ available: r.available, enabled: r.enabled });
        setBtDevices(r.devices);
      } else if (s === "windows") {
        setWinPrinters(await listWindowsPrinters());
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setCfg(getPrinterConfig());
    refresh();
  }, [open, refresh]);

  const update = (patch) => setCfg((c) => savePrinterConfig({ ...c, ...patch }));

  const testPrint = async () => {
    setTesting(true);
    const r = await printSlip(buildTestReceipt(businessName, formatPrice));
    setTesting(false);
    if (r.ok) {
      toast.success(
        r.method === "bluetooth" ? "Test slip sent to the Bluetooth printer"
          : r.method === "windows-raw" ? "Test slip sent to the Windows printer"
          : r.method === "android-system" ? "Opened the Android print sheet"
          : "Print dialog opened",
      );
    } else {
      toast.error("Test print failed", { description: r.error });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="printer-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Printer className="h-5 w-5" /> Printer setup</DialogTitle>
          <DialogDescription>
            {shell === "android" && "Slips print silently over Bluetooth to the printer you pick here. Pair the printer in Android Bluetooth settings first."}
            {shell === "windows" && "Slips go straight to the Windows printer — no print dialog. Leave the printer blank to use the Windows default."}
            {shell === "browser" && "In a browser the print dialog always appears. For one-tap printing use the NFC Solutions app, or launch Edge/Chrome with --kiosk-printing."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {shell === "android" ? (
            <section className="space-y-2" data-testid="printer-bluetooth-section">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold flex items-center gap-1.5"><Bluetooth className="h-4 w-4" /> Bluetooth printer</p>
                <Button variant="ghost" size="sm" className="h-8 rounded-lg" onClick={refresh} disabled={loading} data-testid="printer-refresh-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
              {!btState.available ? (
                <p className="text-xs text-destructive">This device has no Bluetooth.</p>
              ) : !btState.enabled ? (
                <p className="text-xs text-amber-700 dark:text-amber-400">Bluetooth is turned off — switch it on and refresh.</p>
              ) : btDevices.length === 0 ? (
                <p className="text-xs text-muted-foreground">No paired devices. Pair the printer in Android Settings → Bluetooth, then refresh.</p>
              ) : (
                <div className="max-h-48 space-y-1.5 overflow-y-auto">
                  {btDevices.map((d) => {
                    const on = cfg.btAddress === d.address;
                    return (
                      <button
                        key={d.address}
                        type="button"
                        onClick={() => update({ btAddress: on ? "" : d.address, btName: on ? "" : d.name })}
                        className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors ${on ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}
                        data-testid={`printer-bt-device-${d.address.replace(/:/g, "")}`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{d.name || "Unnamed device"}</span>
                          <span className={`block text-[11px] ${on ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{d.address}{d.likelyPrinter ? " · printer" : ""}</span>
                        </span>
                        {on ? <Check className="h-4 w-4 shrink-0" /> : null}
                      </button>
                    );
                  })}
                </div>
              )}
              {!cfg.btAddress ? (
                <p className="text-[11px] text-muted-foreground">No printer selected — slips will open the Android print sheet instead.</p>
              ) : null}
            </section>
          ) : null}

          {shell === "windows" ? (
            <section className="space-y-2" data-testid="printer-windows-section">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Windows printer</p>
                <Button variant="ghost" size="sm" className="h-8 rounded-lg" onClick={refresh} disabled={loading} data-testid="printer-refresh-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
              <div className="max-h-48 space-y-1.5 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => update({ winPrinterName: "" })}
                  className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm ${!cfg.winPrinterName ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}
                  data-testid="printer-win-default"
                >
                  <span>Windows default{winPrinters.default ? ` (${winPrinters.default})` : ""}</span>
                  {!cfg.winPrinterName ? <Check className="h-4 w-4" /> : null}
                </button>
                {winPrinters.printers.map((name) => {
                  const on = cfg.winPrinterName === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => update({ winPrinterName: name })}
                      className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm ${on ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}
                      data-testid={`printer-win-${name.replace(/\W+/g, "-")}`}
                    >
                      <span className="truncate">{name}</span>
                      {on ? <Check className="h-4 w-4 shrink-0" /> : null}
                    </button>
                  );
                })}
              </div>
              <Input
                value={cfg.winPrinterName}
                onChange={(e) => update({ winPrinterName: e.target.value })}
                placeholder="…or type the exact printer name"
                className="h-10 rounded-xl"
                data-testid="printer-win-name-input"
              />
            </section>
          ) : null}

          <section className="grid grid-cols-2 gap-3" data-testid="printer-paper-section">
            <div>
              <p className="mb-1.5 text-sm font-semibold">Paper width</p>
              <div className="grid grid-cols-2 gap-1.5">
                {[80, 58].map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => update({ paperWidth: w })}
                    className={`h-10 rounded-xl border text-sm font-medium ${Number(cfg.paperWidth) === w ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}
                    data-testid={`printer-paper-${w}`}
                  >
                    {w} mm
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-sm font-semibold">Copies</p>
              <div className="grid grid-cols-3 gap-1.5">
                {[1, 2, 3].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => update({ copies: n })}
                    className={`h-10 rounded-xl border text-sm font-medium ${Number(cfg.copies || 1) === n ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`}
                    data-testid={`printer-copies-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {shell !== "browser" ? (
            <label className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-sm" data-testid="printer-cut-toggle">
              <span>Auto-cut after each slip</span>
              <Switch checked={cfg.cut !== false} onCheckedChange={(v) => update({ cut: Boolean(v) })} />
            </label>
          ) : null}

          {error ? <p className="text-xs text-destructive" data-testid="printer-error">{error}</p> : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} data-testid="printer-close-button">Done</Button>
          <Button
            className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
            onClick={testPrint}
            disabled={testing}
            data-testid="printer-test-button"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            Print test slip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
